// components/RichTextEditor.tsx
import React, { useState } from 'react';
import { sanitizeHtml } from '../../lib/sanitizeHtml';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({ 
  value, 
  onChange, 
  placeholder,
  className = ''
}) => {
  const [isPreview, setIsPreview] = useState(false);

  const addTag = (tag: string) => {
    const tags: Record<string, string> = {
      b: '<strong class="font-semibold">testo in grassetto</strong>',
      i: '<em class="italic">testo in corsivo</em>',

      h2: '<h2 class="text-2xl font-bold mt-4 mb-2">Titolo Sezione</h2>',
      h3: '<h3 class="text-xl font-semibold mt-3 mb-2">Sottotitolo</h3>',
      p: '<p class="mb-4">Paragrafo di testo descrittivo.</p>',
      ul: '<ul class="list-disc list-inside mb-4">\n  <li>Elemento lista 1</li>\n  <li>Elemento lista 2</li>\n</ul>',
      table: `<table class="min-w-full border-collapse border border-border mb-4">
  <thead>
    <tr class="bg-muted">
      <th class="border border-border px-3 py-2">Caratteristica</th>
      <th class="border border-border px-3 py-2">Valore</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td class="border border-border px-3 py-2">Materiale</td>
      <td class="border border-border px-3 py-2">Acciaio Inox</td>
    </tr>
  </tbody>
</table>`,
      bold: '<strong class="font-semibold">testo in grassetto</strong>',
      italic: '<em class="italic">testo in corsivo</em>',
      link: '<a href="https://example.com" class="text-primary hover:underline" target="_blank">testo del link</a>'
    };

    // Inserisce il tag alla posizione corrente del cursore o alla fine
    onChange(value + '\n' + tags[tag]);
  };


  return (
    <div className={`border border-border rounded-lg overflow-hidden ${className}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 p-3 border-b border-border bg-muted">
        <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Formattazione:</span>
        
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => addTag('b')}
            className="px-3 py-2 md:px-2 md:py-1 text-xs bg-card border border-border rounded-md font-semibold text-foreground hover:bg-primary/10 hover:text-primary transition-colors"
            title="Grassetto"
          >
            <strong>B</strong>
          </button>
          
          <button
            type="button"
            onClick={() => addTag('i')}
            className="px-3 py-2 md:px-2 md:py-1 text-xs bg-card border border-border rounded-md font-semibold text-foreground hover:bg-primary/10 hover:text-primary transition-colors"
            title="Corsivo"
          >
            <em>I</em>
          </button>
        </div>

        <div className="flex items-center gap-1">
          {['h2', 'h3', 'p'].map(tag => (
            <button
              key={tag}
              type="button"
              onClick={() => addTag(tag)}
              className="px-3 py-2 md:px-2 md:py-1 text-xs bg-card border border-border rounded-md font-semibold text-foreground hover:bg-primary/10 hover:text-primary transition-colors capitalize"
            >
              {tag}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1">
          {['ul', 'table'].map(tag => (
            <button
              key={tag}
              type="button"
              onClick={() => addTag(tag)}
              className="px-3 py-2 md:px-2 md:py-1 text-xs bg-card border border-border rounded-md font-semibold text-foreground hover:bg-primary/10 hover:text-primary transition-colors capitalize"
            >
              {tag === 'ul' ? 'Lista' : 'Tabella'}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => addTag('link')}
          className="px-3 py-2 md:px-2 md:py-1 text-xs bg-card border border-border rounded-md font-semibold text-foreground hover:bg-primary/10 hover:text-primary transition-colors"
          title="Inserisci link"
        >
          🔗 Link
        </button>

        <button
          type="button"
          onClick={() => setIsPreview(!isPreview)}
          className="ml-auto px-3 py-1 text-xs bg-primary/10 text-primary rounded-md hover:bg-primary/20 transition-colors font-medium"
        >
          {isPreview ? '✏️ Modifica' : '👁️ Anteprima'}
        </button>
      </div>

      {/* Editor/Preview */}
      <div className="bg-card">
        {isPreview ? (
          <div className="p-4 min-h-[200px]">
            {value ? (
              <div 
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }}
              />
            ) : (
              <div className="text-muted-foreground text-center py-8">
                <div className="text-4xl mb-2">👁️</div>
                <p>Nessun contenuto da visualizzare in anteprima</p>
                <p className="text-sm">Torna alla modalità modifica per aggiungere contenuto</p>
              </div>
            )}
          </div>
        ) : (
          <div className="relative">
            <textarea
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="w-full px-4 py-3 text-sm font-mono focus:outline-none resize-y min-h-[200px]"
              placeholder={placeholder || "Inserisci il contenuto in formato HTML..."}
              spellCheck="false"
            />
            <div className="absolute bottom-2 right-2">
              <span className="text-xs text-muted-foreground bg-card px-2 py-1 rounded border border-border">
                {value.length} caratteri
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Istruzioni */}
      {!isPreview && (
        <div className="p-3 bg-muted border-t border-border">
          <p className="text-xs text-muted-foreground">
            <strong>Suggerimenti:</strong> Usa i pulsanti per inserire elementi preformattati o 
            scrivi direttamente in HTML. Supportati: h2, h3, p, ul, li, table, strong, em, a.
          </p>
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;